import React, { useState, useRef } from 'react';
import './PricingSuccess.css';
import { API_ENDPOINTS } from '../config/api';
import { authFetch } from '../utils/authFetch';

const plans = {
  monthly: [
    {
      title: 'Free / Weekly Search Plan',
      price: '$0',
      frequency: 'per month',
      badge: ' ',
      stripeLink: process.env.REACT_APP_STRIPE_LINK_FREE,
      planType: 'Free Tier / 3-URL Daily Search Plan',
      features: [
        '3 career page URL searched weekly',
        'Search frequency: Once every week',
        'For each URL track 3 job titles/Keywords',
        'No customer support',
        'Approx 12 minutes saved every month',
        '7% higher chances of getting a call',
        'Cancel any time',
      ],
      icon: '🔍'
    },
    {
      title: 'Casual Job Seeker/ Daily Search Plan',
      price: '$19.99',
      badge: 'Most popular',
      frequency: 'per month',
      stripeLink: process.env.REACT_APP_STRIPE_LINK_CASUAL,
      planType: 'Casual Job Seeker/ Daily Search Plan',
      features: [
        '25 career page URL searched Daily',
        'Search frequency: Once every Day',
        'For each URL track 3 job titles/Keywords',
        'Basic customer support (72 hours)',
        'Approx 5 hours saved every month',
        '11% higher chances of getting a call',
        'Cancel any time',
      ],
      icon: '📅'
    },
    {
      title: 'Active Job Seeker/ Hourly Search Plan',
      price: '$49.99',
      frequency: 'per month',
      badge: ' ',
      stripeLink: process.env.REACT_APP_STRIPE_LINK_ACTIVE,
      planType: 'Active Job Seeker/ Hourly Search Plan',
      features: [
        '50 career page URL searched Hourly',
        'Search frequency: Once every Hour',
        'For each URL track 3 job titles/Keywords',
        'Advanced customer support (24 hours)',
        'Approx 300 hours saved every month',
        '37% higher chances of getting a call',
        'Cancel any time',
      ],
      icon: '⚡'
    },
  ],
  yearly: [
    {
      title: 'Casual Job Seeker/ Daily Search Plan',
      price: '$19.99',
      frequency: 'per month ($239.88 billed annually)',
      badge: 'Most popular',
      stripeLink: null,
      planType: 'daily',
      features: [
        '25 career page URL searched Daily',
        'Search frequency: Once every Day',
        'For each URL track 3 job titles/Keywords',
        'Basic customer support (72 hours)',
        'Approx 5 hours saved every month',
        '11% higher chances of getting a call',
        'Cancel any time',
      ],
      icon: '📅'
    },
    {
      title: 'Active Job Seeker/ Hourly Search Plan',
      price: '$49.99',
      frequency: 'per month ($599.88 billed annually)',
      badge: ' ',
      stripeLink: null,
      planType: 'hourly',
      features: [
        '50 career page URL searched Hourly',
        'Search frequency: Once every Hour',
        'For each URL track 3 job titles/Keywords',
        'Advanced customer support (24 hours)',
        'Approx 300 hours saved every month',
        '37% higher chances of getting a call',
        'Cancel any time',
      ],
      icon: '⚡'
    },
  ],
};

const Pricing = () => {
  const [mode, setMode] = useState('monthly');
  const [userPlan, setUserPlan] = useState(null);
  const [showComparisonModal, setShowComparisonModal] = useState(false);
  const cardRefs = useRef([]);

  React.useEffect(() => {
    const fetchUserPlan = async () => {
      const userId = localStorage.getItem('userEmail') || 'defaultUserId';
      try {
        const response = await authFetch(
          `${API_ENDPOINTS.GET_USER_INFO}?user_id=${encodeURIComponent(userId)}`
        );
        const text = await response.text();
        if (text && typeof text === 'string' && text.includes('PlanType')) {
          const match = text.match(/'PlanType':\s*'([^']+)'/);
          if (match && match[1]) {
            const planType = match[1].toLowerCase();
            setUserPlan(planType);
          }
        }
      } catch (error) {
        console.error('Error fetching user info:', error);
      }
    };
    fetchUserPlan();
  }, []);

  const createFormBackups = () => {
    const pendingUrl = localStorage.getItem('pendingUrl');
    const pendingKeywordsList = localStorage.getItem('pendingKeywordsList');
    const pendingKeyword = localStorage.getItem('pendingKeyword');

    if (pendingUrl) {
      localStorage.setItem('backup_pendingUrl', pendingUrl);
      localStorage.setItem('stripe_backup_url', pendingUrl);
      sessionStorage.setItem('form_backup_url', pendingUrl);
    }

    if (pendingKeywordsList) {
      localStorage.setItem('backup_pendingKeywordsList', pendingKeywordsList);
      localStorage.setItem('stripe_backup_keywords', pendingKeywordsList);
      sessionStorage.setItem('form_backup_keywords', pendingKeywordsList);
    }

    if (pendingKeyword) {
      localStorage.setItem('backup_pendingKeyword', pendingKeyword);
      localStorage.setItem('stripe_backup_keyword', pendingKeyword);
      sessionStorage.setItem('form_backup_keyword', pendingKeyword);
    }

    const userEmail = localStorage.getItem('userEmail');
    const isLoggedIn = localStorage.getItem('isLoggedIn');

    if (userEmail) {
      localStorage.setItem('backup_userEmail', userEmail);
      sessionStorage.setItem('backup_userEmail', userEmail);
    }

    if (isLoggedIn) {
      localStorage.setItem('backup_isLoggedIn', isLoggedIn);
      sessionStorage.setItem('backup_isLoggedIn', isLoggedIn);
    }
  };

  const handleSubscribe = (plan) => {
    if (plan.stripeLink) {
      createFormBackups();
      createConfetti();
      setTimeout(() => {
        window.location.href = plan.stripeLink;
      }, 800);
    } else {
      alert('Payment setup for this plan is coming soon.');
    }
  };

  const toggleMode = (newMode) => {
    setMode(newMode);
  };

  const createConfetti = () => {
    const confettiContainer = document.createElement('div');
    confettiContainer.className = 'confetti-container';
    document.body.appendChild(confettiContainer);

    for (let i = 0; i < 100; i++) {
      const confetti = document.createElement('div');
      confetti.className = 'confetti';
      confetti.style.left = Math.random() * 100 + 'vw';
      confetti.style.animationDuration = (Math.random() * 3 + 2) + 's';
      confetti.style.background = `hsl(${Math.random() * 360}, 100%, 50%)`;
      confettiContainer.appendChild(confetti);
    }

    setTimeout(() => {
      document.body.removeChild(confettiContainer);
    }, 3000);
  };

  return (
    <div className="pricing-container">
      <div className="pricing-header fade-up fade-delay-1">
        <h1>Choose Your Best Plan</h1>
        <p className="subtitle">Find the perfect plan for your job search needs</p>
        <button
          className="compare-plans-btn"
          onClick={() => setShowComparisonModal(!showComparisonModal)}
        >
          Compare Plans
        </button>
      </div>

      <div className="plan-cards">
        {plans[mode].map((plan, index) => {
          const isCurrent = plan.planType?.toLowerCase() === userPlan?.toLowerCase();
          return (
            <div
              className={`card ${plan.badge.includes('Most') ? 'popular' : ''} ${isCurrent ? 'current' : ''} fade-up fade-delay-${index + 1}`}
              key={index}
              ref={el => cardRefs.current[index] = el}
            >
              {plan.badge.trim() && (
                <div className="badge">{plan.badge}</div>
              )}
              <div className="card-header">
                <div className="plan-icon">{plan.icon}</div>
                <h2>{plan.title}</h2>
              </div>
              <div className="price-container">
                <p className="price">{plan.price}</p>
                <p className="frequency">{plan.frequency}</p>
              </div>
              {isCurrent ? (
                <div className="current-plan-label">
                  <span className="checkmark">✓</span> Your Active Plan
                </div>
              ) : (
                <button
                  className="subscribe-btn"
                  onClick={() => handleSubscribe(plan)}
                >
                  Subscribe <span className="btn-arrow">→</span>
                </button>
              )}
              <ul className="features-list">
                {plan.features.map((feature, idx) => (
                  <li key={idx}>
                    <span className="feature-bullet">•</span> {feature}
                  </li>
                ))}
              </ul>
              {isCurrent && (
                <div className="manage-subscription">
                  <a
                    href={process.env.REACT_APP_STRIPE_PORTAL_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Manage Subscription
                  </a>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {showComparisonModal && (
        <div className="comparison-modal">
          <div className="modal-content fade-in-only">
            <button className="close-btn" onClick={() => setShowComparisonModal(false)}>×</button>
            <h2>Plan Comparison</h2>
            <table className="comparison-table">
              <thead>
                <tr>
                  <th>Feature</th>
                  <th>Free / Weekly</th>
                  <th>Casual Job Seeker</th>
                  <th>Active Job Seeker</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>URLs Searched</strong></td>
                  <td>3 weekly</td>
                  <td>25 daily</td>
                  <td>50 hourly</td>
                </tr>
                <tr>
                  <td><strong>Search Frequency</strong></td>
                  <td>Weekly</td>
                  <td>Daily</td>
                  <td>Hourly</td>
                </tr>
                <tr>
                  <td><strong>Job Titles per URL</strong></td>
                  <td>3</td>
                  <td>3</td>
                  <td>3</td>
                </tr>
                <tr>
                  <td><strong>Customer Support</strong></td>
                  <td>None</td>
                  <td>72 hours</td>
                  <td>24 hours</td>
                </tr>
                <tr>
                  <td><strong>Time Saved</strong></td>
                  <td>12 min/month</td>
                  <td>5 hrs/month</td>
                  <td>300 hrs/month</td>
                </tr>
                <tr>
                  <td><strong>Higher Callback Chances</strong></td>
                  <td>7%</td>
                  <td>11%</td>
                  <td>37%</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Pricing;